import { FeatureName } from './featurePermission';

/**
 * Os desfechos do aceite (§7.1). São TRÊS, e não "erro ou sucesso" — a tela precisa distinguir
 * "confirme seu e-mail" de "esta conta não é a convidada", que são caminhos de saída diferentes.
 */
export enum AcceptOutcome {
  ACCEPTED = 'ACCEPTED',
  /** O convite segue `PENDING` e o token continua valendo: confirmar e voltar conclui o aceite. */
  EMAIL_CONFIRMATION_REQUIRED = 'EMAIL_CONFIRMATION_REQUIRED',
}

export type AcceptInviteResponse = {
  outcome: AcceptOutcome;
  collaboratorId?: number;
};

/** Ações da matriz, no vocabulário de §4 — `delete`, não `remove`. */
export type FeatureAction = 'view' | 'create' | 'update' | 'delete' | 'export';

/**
 * Conjunto ABSOLUTO de permissões (D4). Ausência de uma feature aqui significa "não concedida", e
 * não "mantém o que tinha": não há o que manter.
 */
export type GrantedPermissions = Partial<Record<FeatureName, FeatureAction[]>>;

/**
 * O recorte dos relatórios. `COMMISSION` é o de sempre — a pessoa vê o que a comissão dela alcança.
 * `PRODUCT` é o produto inteiro, e a `report-api` ainda o recusa (§6.4.1.2); enquanto isso, a
 * listagem só o exibe.
 */
export enum ReportScope {
  COMMISSION = 'COMMISSION',
  PRODUCT = 'PRODUCT',
}

export enum CollaboratorStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  REVOKED = 'REVOKED',
  EXPIRED = 'EXPIRED',
}

/** O papel de quem tem acesso. `COLLABORATOR` é quem entrou pela Central. */
export type AccessRole =
  | 'OWNER'
  | 'COPRODUCER'
  | 'AFFILIATED'
  | 'REFERRER'
  | 'COLLABORATOR';

export type Collaborator = {
  /**
   * O id da linha de `platform_collaborator`, e NULO para quem entra por vínculo de comissão —
   * essas pessoas não têm uma. É ele que decide se a linha pode ser encerrada aqui: revogar
   * coprodução ou afiliação é no fluxo de comissão, não na Central.
   */
  id: number | null;
  role: AccessRole;
  /** Nulo enquanto o convite está pendente: ainda não há conta ligada. */
  name: string | null;
  inviteEmail: string;
  status: CollaboratorStatus;
  /** Nulo em duas situações: convite ainda não aceito, e vínculo sem conta de plataforma. */
  platformUserId: number | null;
  /** Nulo para vínculo de comissão: não houve convite. */
  invitedAt: string | null;
  /** Só o pendente tem prazo. Depois do aceite o acesso não vence — o que encerra é revogação. */
  expiresAt: string | null;
  /**
   * Nulo quando não existe grant, e `{}` quando existe e está vazio.
   *
   * A diferença importa: vazio é "não pode nada" (D4), e nulo é "ninguém definiu" — quem entra por
   * comissão sem grant cai na matriz do papel dele.
   */
  permissions: GrantedPermissions | null;
  reportScope: ReportScope | null;
  /**
   * O que a pessoa PODE hoje naquele produto, resolvido pela `platform-api`.
   *
   * Vem do servidor porque a regra mora lá (§6.2): papel único por precedência, grant absoluto por
   * cima, piso do coprodutor somado — e `REFERRER` normalizado como a resolução faz. O front chegou
   * a reproduzir isso e é o começo do drift que a Central existe para fechar.
   *
   * Vazio para convite pendente e para vínculo sem conta de plataforma: nos dois casos não há
   * acesso a exercer, e o campo promete o que a pessoa pode, não o que foi proposto.
   */
  effectivePermissions: GrantedPermissions;
  /**
   * Os pares que o PISO do coprodutor (§5.1) garante e que o grant NÃO diz.
   *
   * Eles chegam misturados no efetivo e são imunes a grant — desmarcar não tira. Mas gravá-los como
   * par explícito num grant de escopo de produto muda o que significam: de "a comissão desta
   * pessoa" para "a comunidade inteira", que é o oposto do que o piso protege. Por isso vêm
   * separados: a tela mostra como garantido e não submete.
   *
   * Vazio para quem não é coprodutor — e também para o par que o grant já concede, que não é "só
   * piso" e precisa continuar sendo gravado.
   */
  protectedPermissions: GrantedPermissions;
  /**
   * O que o PAPEL dá por si — a resolução com `grant: null`.
   *
   * É a linha de base que o `validateGrant` usa para decidir se um grant ALARGA uma feature de
   * produto (`reportBotContacts`), e vem do servidor pelo mesmo motivo que o efetivo: reproduzir a
   * resolução aqui é criar a segunda implementação que erra em silêncio.
   *
   * Não dá para usar `effectivePermissions` no lugar: ele já embute o grant, então "foi concedido
   * antes" fica indistinguível de "o papel dá" — e o rebaixamento de escopo PRODUCT→COMMISSION
   * passaria pela tela para ser recusado pela escrita. `protectedPermissions` também não serve: o
   * piso é outra coisa e não inclui feature de produto.
   *
   * Vazio para colaboração pura, que não tem papel de comissão: para ela, conceder qualquer feature
   * de produto é alargamento.
   *
   * Opcional porque foi ACRESCENTADO (platform-api#273): contra uma API mais velha ele chega
   * `undefined`, e a tela não pode travar por falta dele.
   */
  rolePermissions?: GrantedPermissions;
  /** Só rótulo: o preset é expandido em permissões explícitas no convite e não participa da resolução. */
  presetLabel: string | null;
};

/**
 * Um preset de cargo, como a `platform-api` o define (§4).
 *
 * É ponto de partida, não contrato: o conjunto é expandido em permissões explícitas no convite, e o
 * rótulo não participa da resolução. Mudar a definição lá não repermissiona ninguém que já foi
 * convidado.
 */
export type CollaboratorPreset = {
  id: string;
  label: string;
  description: string;
  permissions: GrantedPermissions;
  reportScope: ReportScope;
};

export type InviteCollaboratorPayload = {
  email: string;
  /** Conjunto ABSOLUTO (D4): o que não está aqui não é concedido. */
  permissions: GrantedPermissions;
  reportScope?: ReportScope;
  /** Só rótulo. */
  presetLabel?: string;
};

/**
 * Uma linha da auditoria do produto (§8), como a aba Atividade a lê.
 *
 * Traz IP e navegador de TERCEIROS, e por isso a leitura é owner-only na `platform-api` — direito
 * separado de listar quem tem acesso, com checagem explícita de titularidade em vez de feature
 * concedível por grant (§6.1.1).
 */
export type ProductAuditEntry = {
  id: number;
  creationDate: string;
  /** `feature:acao` no esquema de §8.2, ou um dos nomes curados de `AuditLogAction`. */
  action: string;
  entity: string;
  recordId: number | null;
  updatedColumns: Record<string, unknown> | null;
  ip: string | null;
  browserData: string | null;
  /**
   * Falso é informação, não erro: a tentativa NEGADA é gravada com o bit em zero (D9), e é
   * justamente a classe de evento que a auditoria existe para mostrar.
   */
  successful: boolean;
  platformUserId: number | null;
  /** Nulo quando a conta não existe mais — o log é append-only e o rastro fica (§8.4). */
  authorName: string | null;
};

/** Uma página da auditoria. `count` é o total do produto, não o da página. */
export type ProductActivityPage = {
  data: ProductAuditEntry[];
  /** Quantos eventos o produto tem ao todo — não "quantos faltam". */
  count: number;
  limit: number;
  /**
   * De onde continuar, ou `null` quando acabou.
   *
   * A paginação é por CURSOR porque a auditoria recebe linhas enquanto alguém a lê: com `offset`,
   * a inserção entra na frente — a ordem é decrescente — e faz uma linha repetir enquanto outra é
   * pulada. Valor opaco: a tela só devolve o que recebeu.
   */
  nextCursor: string | null;
};
